import { QueryKeys } from '@shared/domain/queryKeys'
import { useApiClient } from '@shared/hooks/useApiClient'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Bet, ProofPoint, Stage } from '../domain/Bet.domain'

interface UpdateProofPointInput {
  id: string
  betId: string
  fromStage: Stage
  text?: string
  date?: string
  score?: number
  ownerId?: string | null
  /** Pass to move the card to a different stage */
  stage?: Stage
}

export const useUpdateProofPoint = () => {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      betId: _betId,
      fromStage: _from,
      ...data
    }: UpdateProofPointInput) => {
      const response = await api.patch<ProofPoint>(`/proof-points/${id}`, data)
      return response.data
    },
    onMutate: async ({ id, betId, fromStage, stage: toStage, ...changes }) => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.Bets] })
      const previous = queryClient.getQueryData<Bet[]>([QueryKeys.Bets])

      queryClient.setQueryData<Bet[]>([QueryKeys.Bets], (old) =>
        old?.map((b) => {
          if (b.id !== betId) return b

          // Update the proof point in-place (no stage change)
          if (!toStage || toStage === fromStage) {
            return {
              ...b,
              [fromStage]: b[fromStage].map((p) =>
                p.id === id ? { ...p, ...changes } : p
              ),
            }
          }

          // Move to another stage
          const proof = b[fromStage].find((p) => p.id === id)
          if (!proof) return b

          const moved = { ...proof, ...changes }
          const newStageItems = [...b[toStage], moved].sort((a, b) =>
            a.text.localeCompare(b.text)
          )

          return {
            ...b,
            [fromStage]: b[fromStage].filter((p) => p.id !== id),
            [toStage]: newStageItems,
          }
        })
      )

      return { previous }
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData([QueryKeys.Bets], context?.previous)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.Bets] })
    },
  })
}
